import { useEffect, useMemo, useRef, useState } from "react";
import {
  apiSearchWorkspaceMessages,
  type WorkspaceSearchMessage,
} from "../lib/authApi";
import { parseWorkspaceTopicFileEnvelope, parseWorkspaceTopicImageEnvelope } from "../lib/crypto";
import { AppIcon } from "./AppIcon";

export type WorkspaceSearchSelectArgs = {
  messageId: string;
  topicId: string;
  topicName: string;
  createdAt: number;
};

type WorkspaceMessageSearchPanelProps = {
  workspaceId: string;
  currentTopicId?: string | null;
  onSelectResult: (args: WorkspaceSearchSelectArgs) => void;
  onClose: () => void;
};

function formatSearchTimestamp(ts: number): string {
  return new Date(ts).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function previewFromBody(body: string): string {
  const image = parseWorkspaceTopicImageEnvelope(body);
  if (image) {
    const caption = image.caption.trim();
    return caption ? (caption.length > 140 ? caption.slice(0, 140) + "…" : caption) : "Фото";
  }
  const file = parseWorkspaceTopicFileEnvelope(body);
  if (file) {
    const caption = file.caption.trim();
    if (caption) return caption.length > 140 ? caption.slice(0, 140) + "…" : caption;
    const fn = file.attachment.filename;
    return fn ? (fn.length > 140 ? fn.slice(0, 140) + "…" : fn) : "Файл";
  }
  const trimmed = body.trim();
  return trimmed.length > 140 ? trimmed.slice(0, 140) + "…" : trimmed || "Пустое сообщение";
}

export function WorkspaceMessageSearchPanel({
  workspaceId,
  currentTopicId = null,
  onSelectResult,
  onClose,
}: WorkspaceMessageSearchPanelProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<WorkspaceSearchMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultsScope, setResultsScope] = useState<"topic" | "workspace" | null>(null);
  const normalizedQuery = query.trim();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!normalizedQuery) {
      setResults([]);
      setError(null);
      setLoading(false);
      setResultsScope(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      const runSearch = async () => {
        if (currentTopicId) {
          const topicScoped = await apiSearchWorkspaceMessages(workspaceId, normalizedQuery, {
            topicId: currentTopicId,
            limit: 20,
          });
          if (topicScoped.messages.length > 0) {
            return { messages: topicScoped.messages, scope: "topic" as const };
          }
        }
        const workspaceScoped = await apiSearchWorkspaceMessages(workspaceId, normalizedQuery, { limit: 20 });
        return { messages: workspaceScoped.messages, scope: "workspace" as const };
      };
      void runSearch()
        .then((data) => {
          if (cancelled) return;
          setResults(data.messages);
          setResultsScope(data.scope);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setResults([]);
          setResultsScope(null);
          setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [currentTopicId, workspaceId, normalizedQuery]);

  const emptyState = useMemo(() => {
    if (!normalizedQuery) return "Введите запрос";
    if (loading) return "Поиск…";
    if (error) return error;
    return results.length === 0
      ? currentTopicId
        ? "Ничего не найдено ни в этой теме, ни в других темах workspace"
        : "Ничего не найдено"
      : null;
  }, [currentTopicId, error, loading, normalizedQuery, results.length]);

  const helperText = useMemo(() => {
    if (loading || error || results.length === 0) return null;
    if (currentTopicId && resultsScope === "workspace") {
      return "В этой теме ничего не найдено. Похожие результаты в других темах:";
    }
    return resultsScope === "topic" ? "Совпадения в текущей теме:" : null;
  }, [currentTopicId, error, loading, results.length, resultsScope]);

  return (
    <div className="workspace-search-panel" role="search">
      <div className="workspace-search-panel__bar">
        <span className="workspace-search-panel__icon" aria-hidden="true">
          <AppIcon name="search" className="workspace-search-panel__icon-svg" size={16} />
        </span>
        <input
          ref={inputRef}
          type="search"
          className="workspace-search-panel__input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={currentTopicId ? "поиск по теме…" : "поиск по workspace…"}
          onKeyDown={(e) => {
            if (e.key === "Escape") onClose();
          }}
        />
        <button type="button" className="workspace-search-panel__close" onClick={onClose} aria-label="Закрыть поиск">
          <AppIcon name="x" size={16} />
        </button>
      </div>
      <div className="workspace-search-panel__results">
        {emptyState ? (
          <div className="workspace-search-panel__state">{emptyState}</div>
        ) : (
          <>
            {helperText ? <div className="workspace-search-panel__helper">{helperText}</div> : null}
            <ul className="workspace-search-panel__list">
              {results.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    className="workspace-search-panel__item"
                    onClick={() =>
                      onSelectResult({
                        messageId: row.id,
                        topicId: row.topic_id,
                        topicName: row.topic_name,
                        createdAt: row.created_at,
                      })
                    }
                  >
                    <span className="workspace-search-panel__item-top">
                      <span className="workspace-search-panel__topic">#{row.topic_name}</span>
                      <span className="workspace-search-panel__time">{formatSearchTimestamp(row.created_at)}</span>
                    </span>
                    <span className="workspace-search-panel__preview">{previewFromBody(row.body)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
